import {
  Component,
  computed,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { GalleryPhoto } from '@capacitor/camera';
import {
  IonModal,
  IonHeader,
  IonButtons,
  IonToolbar,
  IonButton,
  IonIcon,
  IonContent,
  IonChip,
  IonAvatar,
  IonLabel,
  ActionSheetButton,
  IonActionSheet,
  IonTitle,
  IonFooter,
} from '@ionic/angular/standalone';
import { AuthService } from 'src/app/features/auth/auth.service';
import { ChatFieldComponent } from 'src/app/features/chat/components/chat-field/chat-field.component';
import { MessageContentComponent } from 'src/app/features/chat/components/message-content/message-content.component';
import { SelectImpersonateService } from 'src/app/features/chat/services/select-impersonate.service';
import { Message } from 'src/app/features/chat/types/message';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';
import { DESKTOP_MIN_WIDTH_PX } from 'src/app/utils/responsive-nav-animation';

@Component({
  selector: 're-modal-editor',
  imports: [
    NgTemplateOutlet,
    IonActionSheet,
    IonLabel,
    IonAvatar,
    IonChip,
    IonContent,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    IonModal,
    IonTitle,
    IonFooter,
    MessageContentComponent,
    ChatFieldComponent,
    ...TRANSLATE_IMPORTS,
  ],
  templateUrl: './modal-editor.component.html',
  styleUrls: ['./modal-editor.component.scss'],
})
export class ModalEditorComponent implements OnInit, OnDestroy {
  @ViewChild(IonModal) modal!: IonModal;

  text = model<string>();

  addImage = output();
  sendMessage = output();
  removeImage = output();
  quoteMessageHandler = output();

  imageSelected = input<GalleryPhoto | undefined>();
  allowImage = input<boolean>(true);
  quotedMessage = input<Message | undefined>();
  quotables = input<
    {
      id: string;
      displayName: string;
      type: 'user' | 'char';
    }[]
  >();

  readonly isDesktop = signal(false);

  private readonly platformId = inject(PLATFORM_ID);
  private mediaQuery?: MediaQueryList;
  private readonly onMediaChange = (event: MediaQueryListEvent) => {
    this.isDesktop.set(event.matches);
  };

  impersonateService = inject(SelectImpersonateService);
  private translate = inject(TranslateService);

  profile = toSignal(inject(AuthService).profile$);

  showCharacterPicker = computed(
    () => this.impersonateService.characters().length > 0,
  );

  actionSheetSelectCharButtons = computed<ActionSheetButton[]>(() => {
    return [
      ...this.impersonateService.characters().map((char) => {
        return {
          text: char.name,
          handler: () => {
            this.impersonateService.setImpersonate(char.id);
          },
        };
      }),
      {
        text: this.translate.instant('common.none'),
        handler: () => {
          this.impersonateService.setImpersonate(undefined);
        },
      },
    ];
  });

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.mediaQuery = window.matchMedia(
      `(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`,
    );
    this.isDesktop.set(this.mediaQuery.matches);
    this.mediaQuery.addEventListener('change', this.onMediaChange);
  }

  ngOnDestroy(): void {
    this.mediaQuery?.removeEventListener('change', this.onMediaChange);
  }

  dismiss(): void {
    void this.modal?.dismiss();
  }
}
